import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { createEnrollToken } from './api';
import { DEVICE_NAME_RE, linuxInstallerScript, windowsInstallerScript } from './installer-script';

interface Minted {
  token: string;
  expiresAt: string;
}

/** Hand the browser a generated file — no server round trip, no temp storage. */
function downloadText(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Copy button that degrades to a no-op when the clipboard API is unavailable. */
function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Box
          component="pre"
          sx={{
            flex: 1,
            m: 0,
            p: 1.5,
            bgcolor: 'grey.100',
            borderRadius: 1,
            fontSize: 12,
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}
        >
          {value}
        </Box>
        <Button
          size="small"
          startIcon={<ContentCopyIcon fontSize="small" />}
          onClick={() => {
            void navigator.clipboard?.writeText(value);
            setCopied(true);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Box>
    </Box>
  );
}

export function EnrollDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [customer, setCustomer] = useState('');
  // A customer's own PC: the installer writes desktopConsent, so the person
  // sitting there is asked (Yes/No box) before every screen session. Off by
  // default — a planted box has nobody to ask. Windows only.
  const [askFirst, setAskFirst] = useState(false);
  const [minted, setMinted] = useState<Minted | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const origin = window.location.origin;

  // The name is interpolated into a shell command line — the installer scripts,
  // and the two copy-paste commands below — so it has to survive that intact.
  // Caught HERE, before the token is minted, because every way it used to fail
  // was silent: the generators threw out of an onClick nobody caught (the button
  // simply did nothing), and the copy rows happily produced `--name Office PC`,
  // which hands the installer "Office" and a stray argument.
  const trimmedName = name.trim();
  const nameInvalid = trimmedName !== '' && !DEVICE_NAME_RE.test(trimmedName);

  /**
   * A generator refusing its input must never look like a dead button. This is
   * belt to the field validation's braces: the button is unreachable while the
   * name is bad, so anything landing here is a case we did not anticipate, and
   * it belongs on screen rather than in the console.
   */
  function downloadInstaller(filename: string, build: () => string) {
    try {
      downloadText(filename, build());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not build the installer');
    }
  }

  async function mint() {
    setBusy(true);
    setError(null);
    try {
      setMinted(
        await createEnrollToken({ name: name || undefined, customer: customer || undefined }),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not create token');
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setMinted(null);
    setName('');
    setCustomer('');
    setAskFirst(false);
    setError(null);
    onClose();
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Enroll a device</DialogTitle>
      <DialogContent>
        {!minted ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Device name (optional)"
              placeholder="box-01"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={nameInvalid}
              helperText={
                nameInvalid
                  ? 'No spaces — letters, digits, dot, dash and underscore only. It goes straight into the installer command, so "Office PC" would arrive as "Office".'
                  : 'Letters, digits, dot, dash, underscore. Blank uses the machine’s own hostname.'
              }
              fullWidth
            />
            <TextField
              label="Customer (optional)"
              placeholder="PT Dirgantara Indonesia"
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              fullWidth
            />
            <FormControlLabel
              sx={{ alignItems: 'flex-start', ml: 0 }}
              control={
                <Checkbox
                  checked={askFirst}
                  onChange={(e) => setAskFirst(e.target.checked)}
                  sx={{ mt: -0.5 }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {"Customer's own PC — ask the person before every screen session"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    A Yes/No box on that screen; nothing is shared until they say yes, and no
                    answer within 45 s counts as no. Leave it off for a Dikonek box nobody sits
                    at. Windows only.
                  </Typography>
                </Box>
              }
            />
            <Typography variant="body2" color="text.secondary">
              The token is one-time and expires in 24 hours.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Alert severity="warning">
              This token is shown once. It expires{' '}
              {new Date(minted.expiresAt).toLocaleString()}.
            </Alert>

            {/* Signed in ON the box being enrolled? Then nothing needs copying
                at all — take the installer with the token already inside. */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Installing on this machine — download and run
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() =>
                    downloadInstaller('install-dikonek-tunnel.cmd', () =>
                      windowsInstallerScript(minted.token, trimmedName, origin, askFirst),
                    )
                  }
                >
                  Windows installer
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() =>
                    downloadInstaller('install-dikonek-tunnel.sh', () =>
                      linuxInstallerScript(minted.token, trimmedName, origin),
                    )
                  }
                >
                  Linux installer
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Windows: double-click it and approve the admin prompt. Linux:{' '}
                <code>sudo bash install-dikonek-tunnel.sh</code>. The token is inside the
                file — delete it once the box appears in the list.
              </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary">
              Enrolling a different machine? Open <b>{origin.replace(/^https?:\/\//, '')}/download</b>{' '}
              there and paste the token below — or use the commands.
            </Typography>

            <CopyRow label="Token" value={minted.token} />
            <CopyRow
              label="Linux — run on the box"
              value={`curl -fsSL ${origin}/install.sh | sudo bash -s -- --token ${minted.token}${trimmedName ? ` --name ${trimmedName}` : ''}`}
            />
            <CopyRow
              label="Windows — elevated PowerShell"
              value={`irm ${origin}/install.ps1 | iex; Install-DikonekTunnel -Token ${minted.token}${trimmedName ? ` -Name ${trimmedName}` : ''}${askFirst ? ' -ScreenConsent $true' : ''}`}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{minted ? 'Done' : 'Cancel'}</Button>
        {!minted && (
          <Button variant="contained" onClick={mint} disabled={busy || nameInvalid}>
            {busy ? 'Creating…' : 'Create token'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
